import { Body, Col, Divider, Flex, Shape, styled } from '@teko/ds-desktop';
import { ProductPrice, formatCurrency } from '@teko/teko-ecommerce';
import { DividerStyleEnum } from '@teko/types/shared';
import { useProductDetailState, useTenantConfig } from 'contexts';
import { t } from 'i18n';
import { VariantRow } from 'modules/product-detail/shared/ProductVariantRow';
import { UomItem } from 'modules/product-detail/shared/UomItem';
import { useEffect, useMemo } from 'react';
import { useGetPriceDetailPage } from '../hooks/useGetPriceDetailPage';

export const ProductUnitAndPrice = () => {
  const { isDisplayUom, numberDisplayFormat, isMobile, epicOmni1643Sgc } =
    useTenantConfig();

  const { product, price, setIsReturnVariants } = useProductDetailState();
  const { calculatePrice } = useGetPriceDetailPage();

  const productOptions = product?.productOptions;

  const { uomRow, attributeRows } = useMemo(() => {
    const rows = productOptions?.rows || [];
    return {
      uomRow: rows.find((row) => row.code === 'uom'),
      attributeRows: rows.filter((row) => row.code !== 'uom'),
    };
  }, [productOptions?.rows]);

  const hasUomRow = !!uomRow && uomRow.options.length > 1;

  useEffect(() => {
    setIsReturnVariants?.(hasUomRow);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasUomRow]);

  const renderUomSection = () => {
    if (hasUomRow) {
      return <VariantRow row={uomRow} isUomRow />;
    }

    if (isDisplayUom && product?.uomName) {
      return (
        <Flex
          direction="column"
          backgroundColor="white"
          gap="s4"
          marginTop="s8"
        >
          <Body level={1}>{t('Đơn vị')}:</Body>
          <UomItem />
        </Flex>
      );
    }

    return null;
  };

  if (!productOptions) return null;

  return (
    <>
      <VariantsBlock>
        {/* Render non-UOM variant rows first */}
        {!!attributeRows?.length &&
          attributeRows.map((row, i) => (
            <VariantRow
              key={i}
              row={row}
              showImage={i === 0 || epicOmni1643Sgc}
            />
          ))}

        {/* Render UOM */}
        {renderUomSection()}
      </VariantsBlock>

      <Divider
        dividerStyle={DividerStyleEnum.dashed}
        backgroundColor="white"
        color="divider"
        padding="s0"
      />

      {!hasUomRow && !!price?.finalPrice && !!product?.status?.sellable && (
        <Shape backgroundColor="white" marginBottom={isMobile ? 's0' : 's8'}>
          {!!price?.finalPrice && (
            <StyledProductPrice
              isMobile={isMobile}
              discountPercent={price?.discountPercent}
              latestPrice={
                formatCurrency(
                  price?.finalPrice as number,
                  numberDisplayFormat,
                ) || ''
              }
              supplierRetailPrice={
                formatCurrency(
                  calculatePrice?.supplierRetailPrice as number,
                  numberDisplayFormat,
                ) || ''
              }
              size="l"
              numberDisplayFormat={numberDisplayFormat}
            />
          )}
        </Shape>
      )}
    </>
  );
};

const VariantsBlock = styled(Col)`
  gap: 16px;
`;

const StyledProductPrice = styled(ProductPrice)<{ isMobile?: boolean }>`
  height: auto;
  .att-product-detail-latest-price {
    font-size: ${({ isMobile }) => (isMobile ? '24px' : '32px')};
    line-height: ${({ isMobile }) => (isMobile ? '32px' : '40px')};
  }
  .att-product-detail-retail-price {
    font-size: 13px;
  }
`;
